import { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import { breakpoints, gutters, isMobile, useWindowSize } from 'app/spacing';
import KizenTypography from 'app/kizentypo';
import Loader from 'components/Kizen/Loader';
import { Nav } from 'components/Navigation/NavLayout';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import { DesktopOnlyButton } from 'pages/Common/styles/ControlBar';
import { previewForm } from 'pages/Forms';
import { replaceForm } from 'store/formsPage/forms.redux';
import { setError } from 'store/errors';
import navItems from './navigation';
import BuilderModal from './Builder';
import { useTranslation } from 'react-i18next';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { formQuery } from 'query/forms';
import { snakeToCamelCaseKeys } from 'services/helpers';
import { ensureDeserializedFormPages } from 'xforms/api';
import { FormSettingsPageProvider, useFormSettingsPage } from './context';

const chosenBusinessUrl = `https://${import.meta.env.VITE_EMBED_FRONTEND_DOMAIN}`;

const MobileContainer = styled.div`
  padding: ${gutters.standard};
  text-align: center;
`;

const Container = styled.div`
  margin-bottom: ${gutters.spacing(4)}px;
`;

const setFormTitle = (title, { name = '' } = {}) =>
  name ? `${name} - ${title}` : title;

const Toolbar = ({ routeItems }) => {
  const { t } = useTranslation();
  const params = useParams();
  const { form, onExportResponses, onShowConfirmDeleteModal } =
    useFormSettingsPage();

  return (
    <Nav.Container>
      <Nav.TopNavRow>
        <Nav.TopNavLeftContainer>
          <Nav.TopNavBackButton label={t('back to forms')} path="/forms" />
        </Nav.TopNavLeftContainer>
        <Nav.TopNavCenterContainer>
          <Nav.TopNavHeader label={form.name} />
        </Nav.TopNavCenterContainer>
        <Nav.TopNavRightContainer>
          <DesktopOnlyButton onClick={onShowConfirmDeleteModal} color="red">
            {t('Delete')}
          </DesktopOnlyButton>
          <DesktopOnlyButton
            color="blue"
            onClick={() => previewForm(chosenBusinessUrl, form.slug)}
          >
            {t('Preview')}
          </DesktopOnlyButton>
          <DesktopOnlyButton color="blue" onClick={onExportResponses}>
            {t('Export Responses')}
          </DesktopOnlyButton>
        </Nav.TopNavRightContainer>
      </Nav.TopNavRow>
      <Nav.SubNavigationRow>
        {Object.entries(routeItems).map(([key, route], idx, arr) => {
          const { label } = route;
          const common = {
            isLast: idx === arr.length - 1,
            numberOfLinks: arr.length,
            params,
          };

          if (key === 'build') {
            return (
              <Nav.SubNavigationLink
                key={label}
                route={route}
                saveLocationOnNavigate
                {...common}
              />
            );
          }

          return (
            <Nav.SubNavigationLink key={label} route={route} {...common} />
          );
        })}
      </Nav.SubNavigationRow>
    </Nav.Container>
  );
};

export default function FormSettingsPage({ title }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const [noViewPermissions, setNoViewPermissions] = useState(false);
  const { id } = params;
  const { width } = useWindowSize();
  const mobileDisplay = isMobile(width, breakpoints.lg);
  const preReleaseFeature = usePreReleaseFeatures();

  const query = useQuery(
    formQuery(id, {
      gcTime: 0,
      staleTime: 0,
      throwOnError: false,
      select: (data) => {
        const camel = snakeToCamelCaseKeys(data);

        return {
          ...camel,
          formUi: {
            ...camel.formUi,
            pages: ensureDeserializedFormPages(camel.formUi.pages),
          },
        };
      },
    })
  );

  const form = query.data;

  const routeItems = useMemo(() => {
    const forHide = ['messages', 'rules'];

    return preReleaseFeature
      ? navItems
      : Object.keys(navItems)
          .filter((e) => !forHide.includes(e))
          .reduce((acc, el) => ({ ...acc, [el]: navItems[el] }), {});
  }, [preReleaseFeature]);

  useSetTitleOnLoad(setFormTitle(title(t), form));

  useEffect(() => {
    if (query.isError) {
      const error = query.error;
      if (query.error.isAxiosError) {
        const badPermissions = error.response?.data?.badPermissions;
        if (badPermissions) {
          setNoViewPermissions(true);
        } else {
          dispatch(setError({ code: error?.response?.status }));
        }
      }
    } else if (query.data) {
      dispatch(replaceForm({ form: query.data }));
    }
  }, [query.isError, query.data, query.error, dispatch]);

  const onGoBack = useCallback(() => {
    const formUrl = navItems.settings.path.replace(':id', id);
    if (history.length > 2) {
      history.goBack();
    } else {
      history.push(formUrl);
    }
  }, [history, id]);

  if ((!form && !noViewPermissions) || (form && form.id !== id)) {
    return <Loader loading />;
  }

  const routeComponents = Object.entries(routeItems)
    .filter(([, route]) => !!route.component)
    .map(([, route], index) => (
      <Route
        key={index}
        path={route.path}
        exact={route.exact !== false}
        render={() => <route.component form={form} fetchForm={query.refetch} />}
      />
    ));

  if (mobileDisplay) {
    return (
      <MobileContainer>
        <KizenTypography>
          {t(
            'This page has not been optimized for mobile. Please revisit on a desktop to receive the best experience.'
          )}
        </KizenTypography>
      </MobileContainer>
    );
  }

  return (
    <FormSettingsPageProvider form={form}>
      <Toolbar routeItems={routeItems} />
      <Container>
        <Switch location={location}>{routeComponents}</Switch>
      </Container>
      {/* react-router way of storing a page behind a modal https://v5.reactrouter.com/web/example/modal-gallery */}
      <Route path="/forms/:id/build">
        <BuilderModal form={form} onGoBack={onGoBack} />
      </Route>
    </FormSettingsPageProvider>
  );
}
